import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Card, CardContent } from '@/components/ui/card'
import { AlertCircle } from 'lucide-react'
import { decodeCycleUrl } from '@/lib/cycle-url-encoder'
import { api } from '@/lib/api'
import { FeedbackForm } from '@/components/FeedbackForm'

interface EncodedInfo {
  cycleId?: string
  userId?: string
  participantId?: string
  token?: string
}

interface FeedbackQuestion {
  id: string
  text: string
  questionType: 'text' | 'rating' | 'multiple_choice'
  isRequired: boolean
  sortOrder: number
  options?: any
}

interface FeedbackCycle {
  id: string
  name: string
  description: string
  status: string
  startDate: string
  endDate: string
  targetUserId?: string
  targetUser?: {
    id: string
    firstName: string
    lastName: string
    email: string
    avatar?: string
  }
  questions: FeedbackQuestion[]
  isAnonymous?: boolean
  cycleType: 'regular' | 'recurring' | 'targeted'
}

interface CycleResponse {
  success: boolean
  data?: FeedbackCycle
  error?: string
}

export default function FeedbackCyclePage() {
  const { id } = useParams<{ id: string }>()
  const [cycleId, setCycleId] = useState<string>('')
  const [encodedToken, setEncodedToken] = useState<string | null>(null)
  const [encodedInfo, setEncodedInfo] = useState<EncodedInfo | null>(null)
  const [cycle, setCycle] = useState<FeedbackCycle | null>(null)
  const [participantContext, setParticipantContext] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setError('Missing feedback cycle identifier')
      setLoading(false)
      return
    }

    try {
      const decoded = decodeCycleUrl(id)
      if (decoded) {
        if (decoded.token) {
          setEncodedToken(id)
          setEncodedInfo(decoded)
          if (decoded.cycleId) {
            setCycleId(decoded.cycleId)
          }
          return
        }

        if (decoded.cycleId) {
          setCycleId(decoded.cycleId)
          setEncodedInfo(decoded)
          return
        }
      }

      setCycleId(id)
      setEncodedInfo(null)
    } catch (err) {
      console.warn('Unable to decode cycle identifier', err)
      setCycleId(id)
      setEncodedInfo(null)
    }
  }, [id])

  useEffect(() => {
    if (encodedToken) {
      const fetchPublicCycle = async () => {
        try {
          setLoading(true)
          const result = await api.get<any>(`/feedback/${encodedToken}`)
          if (!result?.success || !result?.data) {
            setError(result?.error || 'Feedback cycle not found')
            console.error('Feedback token lookup failed', result)
            return
          }
          setCycle(result.data.cycle)
          setParticipantContext(result.data.participant ?? null)
        } catch (err) {
          console.error('Failed to load feedback cycle', err)
          setError((err as any)?.message || 'Failed to load feedback cycle')
        } finally {
          setLoading(false)
        }
      }

      fetchPublicCycle()
      return
    }

    if (!cycleId) {
      return
    }

    const fetchCycle = async () => {
      try {
        setLoading(true)
        const result = await api.get<CycleResponse>(`/feedback-cycles/${cycleId}`)
        if (!result.success || !result.data) {
          setError(result.error || 'Feedback cycle not found')
          return
        }
        setCycle(result.data)
      } catch (err) {
        console.error('Failed to load feedback cycle', err)
        setError((err as any)?.message || 'Failed to load feedback cycle')
      } finally {
        setLoading(false)
      }
    }

    fetchCycle()
  }, [cycleId, encodedToken])

  const encodedUserInfo = useMemo(() => {
    if (!encodedInfo) return null
    if (encodedInfo.token) {
      return {
        token: encodedToken,
        participantId: encodedInfo.participantId,
      }
    }
    return {
      userId: encodedInfo.userId,
      participantId: encodedInfo.participantId,
    }
  }, [encodedInfo, encodedToken])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading feedback cycle…</p>
        </div>
      </div>
    )
  }

  if (error || !cycle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Feedback cycle unavailable</h2>
              <p className="text-muted-foreground">{error || 'Unable to load feedback cycle'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <FeedbackForm
      cycle={cycle}
      encodedUserInfo={encodedUserInfo}
      participantContext={participantContext}
    />
  )
}
